import { useState, type ReactNode } from 'react';
import { LogIn, LogOut, Zap, Menu, ChevronDown, Newspaper, Trophy, Landmark, UserCheck, Eye, Briefcase, Bell, Calendar, CalendarClock, Settings as SettingsIcon } from 'lucide-react';
import UserAvatar from '@/components/UserAvatar';
import Wordmark from '@/components/Wordmark';
import { Button } from '@/components/ui/button';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import NavSearch from '@/components/NavSearch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

const ProBadge = () => (
  <span className="ml-auto inline-flex shrink-0 items-center rounded-md border border-border/80 bg-secondary px-1.5 py-0.5 text-[10px] font-medium leading-none text-muted-foreground">
    PRO
  </span>
);

const Navbar = () => {
  const location = useLocation();
  const { user, isPro, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const signalsItems = [
    { to: '/cluster-buys', label: 'Cluster Buys', icon: Zap, proFeature: true },
    { to: '/significant-trades', label: 'Significant Trades', icon: Zap, proFeature: true },
    { to: '/earnings', label: 'Earnings', icon: CalendarClock, proFeature: true },
  ];

  const researchItems = [
    { to: '/leaderboard', label: 'Leaderboard', icon: Trophy, proFeature: false },
    { to: '/track-record', label: 'Track Record', icon: Trophy, proFeature: false },
    { to: '/news', label: 'News', icon: Newspaper, proFeature: false },
    { to: '/calendar', label: 'Calendar', icon: Calendar, proFeature: false },
  ];

  const isSignalsActive = ['/cluster-buys', '/signals', '/significant-trades', '/smart-signals', '/earnings'].includes(location.pathname);
  const isResearchActive = ['/leaderboard', '/track-record', '/news', '/calendar'].includes(location.pathname);

  // Personal pages (alphabetical)
  const personalItems = [
    { to: '/alerts', label: 'Alerts', icon: Bell },
    { to: '/following', label: 'Following', icon: UserCheck },
    { to: '/portfolio', label: 'Portfolio', icon: Briefcase },
    { to: '/watchlist', label: 'Watchlist', icon: Eye },
  ];

  // Mobile all links grouped
  const mobileLinks = [
    { to: '/', label: 'Trades', section: 'main' },
    { to: '/congress', label: 'Congress', section: 'main' },
    ...signalsItems.map(i => ({ ...i, section: 'signals' })),
    ...researchItems.map(i => ({ ...i, section: 'research' })),
    ...(user ? personalItems.map(i => ({ ...i, section: 'personal', proFeature: false })) : []),
    { to: '/pricing', label: 'Pricing', section: 'other' },
  ];

  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-background">
      <div className="container mx-auto flex h-14 items-center justify-between px-4 md:h-14 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <Wordmark size="lg" />
        </Link>

        {/* Desktop nav — underline-on-active, no pill bg */}
        <div className="hidden items-center gap-1 lg:flex">
          {(() => {
            const navLink = (to: string, active: boolean, children: ReactNode) => (
              <Link
                to={to}
                className={`inline-flex h-14 items-center gap-1 border-b-2 px-3 text-xs font-medium transition-colors ${
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {children}
              </Link>
            );
            return (
              <>
                {navLink('/', location.pathname === '/', 'Trades')}
                {navLink('/congress', location.pathname === '/congress', (<><Landmark className="h-3.5 w-3.5" /> Congress</>))}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`inline-flex h-14 items-center gap-1 border-b-2 px-3 text-xs font-medium transition-colors ${
                        isSignalsActive
                          ? 'border-primary text-foreground'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Edges <ChevronDown className="h-3 w-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-44">
                    {signalsItems.map((item) => (
                      <Link key={item.label} to={item.to}>
                        <DropdownMenuItem className="cursor-pointer gap-2 text-xs">
                          <item.icon className="h-3.5 w-3.5" />
                          {item.label}
                          {item.proFeature && <ProBadge />}
                        </DropdownMenuItem>
                      </Link>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`inline-flex h-14 items-center gap-1 border-b-2 px-3 text-xs font-medium transition-colors ${
                        isResearchActive
                          ? 'border-primary text-foreground'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Research <ChevronDown className="h-3 w-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-44">
                    {researchItems.map((item) => (
                      <Link key={item.label} to={item.to}>
                        <DropdownMenuItem className="cursor-pointer gap-2 text-xs">
                          <item.icon className="h-3.5 w-3.5" />
                          {item.label}
                          {item.proFeature && <ProBadge />}
                        </DropdownMenuItem>
                      </Link>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {navLink('/pricing', location.pathname === '/pricing', 'Pricing')}
              </>
            );
          })()}
        </div>

        {/* Right side: search + user */}
        <div className="flex items-center gap-2">
          <NavSearch />

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Account menu"
                  className="hidden lg:inline-flex relative items-center justify-center rounded-full p-1 transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <UserAvatar size={32} />
                  {isPro && (
                    <span className="absolute inset-0 rounded-full ring-2 ring-primary ring-offset-2 ring-offset-background pointer-events-none" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                {personalItems.map((item) => (
                  <Link key={item.label} to={item.to}>
                    <DropdownMenuItem className="cursor-pointer gap-2 text-xs">
                      <item.icon className="h-3.5 w-3.5" />
                      {item.label}
                    </DropdownMenuItem>
                  </Link>
                ))}
                <Link to="/settings">
                  <DropdownMenuItem className="cursor-pointer gap-2 text-xs">
                    <SettingsIcon className="h-3.5 w-3.5" />
                    Settings
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuItem className="cursor-pointer gap-2 text-xs" onClick={signOut}>
                  <LogOut className="h-3.5 w-3.5" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" aria-label="Sign in">
              <Button size="sm" className="h-9 gap-1.5 rounded-md bg-primary px-4 text-xs font-medium text-primary-foreground hover:bg-primary/90 min-h-[44px] md:min-h-0">
                <LogIn className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="hidden sm:inline">Sign In</span>
              </Button>
            </Link>
          )}

          {/* Mobile hamburger */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="min-h-[44px] min-w-[44px] md:hidden"
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileOpen}
                aria-controls="mobile-nav-sheet"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 p-0 flex flex-col">
              <SheetHeader className="border-b border-border/70 px-3 py-1.5 shrink-0">
                <SheetTitle className="text-left"><Wordmark size="base" /></SheetTitle>
              </SheetHeader>
              {user && (
                <div className="border-b border-border/70 px-3 py-1 shrink-0">
                  <div className="flex items-center gap-2">
                    <UserAvatar size={20} />
                    {isPro && <ProBadge />}
                    <span className="truncate text-[11px] text-muted-foreground">{user.email}</span>
                  </div>
                </div>
              )}
              <div className="flex-1 min-h-0 overflow-y-auto flex flex-col p-2">
                {(() => {
                  const rowBase = "flex items-center gap-2.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors";
                  const rowInactive = "border-border/30 bg-transparent text-foreground hover:bg-muted/30 hover:border-border/60";
                  const rowActive = "border-primary/40 bg-primary/10 text-primary";
                  const rowClass = (to: string) =>
                    `${rowBase} ${location.pathname === to ? rowActive : rowInactive}`;
                  const sectionHeader = "mt-3 mb-1 px-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground";
                  const firstSectionHeader = "mb-1 px-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground";
                  return (
                    <>
                      {/* Main */}
                      <div className="flex flex-col gap-1">
                        {mobileLinks.filter(l => l.section === 'main').map((link) => (
                          <Link key={link.label} to={link.to} onClick={() => setMobileOpen(false)} className={rowClass(link.to)}>
                            {link.label}
                          </Link>
                        ))}
                      </div>

                      <div className={sectionHeader}>Edges</div>
                      <div className="flex flex-col gap-1">
                        {mobileLinks.filter(l => l.section === 'signals').map((link) => (
                          <Link key={link.label} to={link.to} onClick={() => setMobileOpen(false)} className={rowClass(link.to)}>
                            {'icon' in link && link.icon && <link.icon className="h-3.5 w-3.5 shrink-0" />}
                            {link.label}
                            {'proFeature' in link && link.proFeature && <ProBadge />}
                          </Link>
                        ))}
                      </div>

                      <div className={sectionHeader}>Research</div>
                      <div className="flex flex-col gap-1">
                        {mobileLinks.filter(l => l.section === 'research').map((link) => (
                          <Link key={link.label} to={link.to} onClick={() => setMobileOpen(false)} className={rowClass(link.to)}>
                            {'icon' in link && link.icon && <link.icon className="h-3.5 w-3.5 shrink-0" />}
                            {link.label}
                            {'proFeature' in link && link.proFeature && <ProBadge />}
                          </Link>
                        ))}
                      </div>

                      {user && mobileLinks.some(l => l.section === 'personal') && (
                        <>
                          <div className={sectionHeader}>Your Lists</div>
                          <div className="flex flex-col gap-1">
                            {mobileLinks.filter(l => l.section === 'personal').map((link) => (
                              <Link key={link.label} to={link.to} onClick={() => setMobileOpen(false)} className={rowClass(link.to)}>
                                {'icon' in link && link.icon && <link.icon className="h-3.5 w-3.5 shrink-0" />}
                                {link.label}
                              </Link>
                            ))}
                            <Link to="/settings" onClick={() => setMobileOpen(false)} className={rowClass('/settings')}>
                              <SettingsIcon className="h-3.5 w-3.5 shrink-0" />
                              Settings
                            </Link>
                          </div>
                        </>
                      )}

                      <div className="mt-3 flex flex-col gap-1">
                        {mobileLinks.filter(l => l.section === 'other').map((link) => (
                          <Link key={link.label} to={link.to} onClick={() => setMobileOpen(false)} className={rowClass(link.to)}>
                            {link.label}
                          </Link>
                        ))}
                      </div>

                      {user && (
                        <button
                          type="button"
                          onClick={() => { setMobileOpen(false); signOut(); }}
                          className="mt-auto flex items-center gap-2.5 rounded-md border border-destructive/40 bg-transparent px-3 py-1.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
                        >
                          <LogOut className="h-3.5 w-3.5 shrink-0" />
                          Sign Out
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
